import { useEffect, useMemo, useState } from "react";
import {
  AtSign,
  Camera,
  Check,
  Globe,
  Mail,
  MessageCircle,
  MessageSquare,
  Music2,
  Pencil,
  PlayCircle,
  X,
} from "lucide-react";
import Button from "../ui/Button";
import Input from "../ui/Input";
import Select from "../ui/Select";
import { readImageAsDataUrl } from "../../utils/readImageAsDataUrl";
import { resolveMediaUrl } from "../../utils/resolveMediaUrl";
import { showError } from "../../utils/toast";

const TYPES = ["Hospital", "Rural bank", "Credit union", "Microfinance", "SACCO", "Finance company"];
const TYPE_OTHER = "__other__";
const TYPE_OPTIONS = [
  ...TYPES.map((value) => ({ value, label: value })),
  { value: TYPE_OTHER, label: "Other" },
];
const REGIONS = [
  "Greater Accra", "Ashanti", "Western", "Western North", "Central", "Eastern",
  "Volta", "Oti", "Northern", "Savannah", "North East", "Upper East",
  "Upper West", "Bono", "Bono East", "Ahafo",
];

const OUTREACH_CHANNELS = [
  { key: "sms", label: "SMS", icon: MessageSquare, iconTone: "bg-sky-100 text-sky-700" },
  { key: "whatsapp", label: "WhatsApp", icon: MessageCircle, iconTone: "bg-emerald-100 text-emerald-700" },
  { key: "email", label: "Email", icon: Mail, iconTone: "bg-violet-100 text-violet-700" },
];

const SOCIAL_FIELDS = [
  { key: "instagram", label: "Instagram", icon: Camera, tone: "bg-gradient-to-br from-amber-100 to-rose-100 text-rose-700" },
  { key: "x", label: "X", icon: AtSign, tone: "bg-slate-900 text-white" },
  { key: "facebook", label: "Facebook", icon: Globe, tone: "bg-blue-100 text-blue-700" },
  { key: "tiktok", label: "TikTok", icon: Music2, tone: "bg-zinc-900 text-white" },
  { key: "youtube", label: "YouTube", icon: PlayCircle, tone: "bg-red-100 text-red-700" },
];

function formFromInstitution(item) {
  const socials = item.socials || {};
  return {
    legal_name: item.legal_name || item.name || "",
    business_type: item.business_type || item.type || "",
    region: item.region || "",
    city: item.city || "",
    gps_address: item.gps_address || item.address || "",
    primary_email: item.primary_email || item.contactEmail || "",
    primary_phone_number: item.primary_phone_number || item.contactPhone || "",
    secondary_email: item.secondary_email || "",
    secondary_phone_number: item.secondary_phone_number || "",
    website: item.website || "",
    logo: resolveMediaUrl(item.logo) || null,
    channels: {
      sms: Boolean(item.channels?.sms),
      whatsapp: Boolean(item.channels?.whatsapp),
      email: Boolean(item.channels?.email),
    },
    socials: {
      instagram: socials.instagram || "",
      x: socials.x || "",
      facebook: socials.facebook || "",
      tiktok: socials.tiktok || "",
      youtube: socials.youtube || "",
    },
  };
}

function changedFields(initial, form) {
  const body = {};
  const scalarFields = [
    "legal_name",
    "business_type",
    "region",
    "city",
    "gps_address",
    "primary_email",
    "primary_phone_number",
    "secondary_email",
    "secondary_phone_number",
    "website",
    "logo",
  ];

  scalarFields.forEach((key) => {
    if (form[key] !== initial[key]) {
      if (key === "logo") {
        body.logo = form.logo || null;
        return;
      }
      const value = String(form[key] || "").trim();
      body[key] = value === "" ? null : value;
    }
  });

  const socials = {};
  let socialChanged = false;
  Object.keys(form.socials).forEach((key) => {
    if (form.socials[key] !== initial.socials[key]) socialChanged = true;
    const value = String(form.socials[key] || "").trim();
    socials[key] = value === "" ? null : value;
  });
  if (socialChanged) body.socials = socials;

  const channels = {};
  let channelsChanged = false;
  Object.keys(form.channels).forEach((key) => {
    if (form.channels[key] !== initial.channels[key]) channelsChanged = true;
    channels[key] = Boolean(form.channels[key]);
  });
  if (channelsChanged) body.channels = channels;

  return body;
}

export default function EditInstitutionModal({ institution, saving, onSave, onClose }) {
  const initial = useMemo(() => formFromInstitution(institution), [institution]);
  const [form, setForm] = useState(initial);
  const changes = useMemo(() => changedFields(initial, form), [form, initial]);
  const hasChanges = Object.keys(changes).length > 0;

  useEffect(() => setForm(initial), [initial]);
  useEffect(() => {
    const onKeyDown = (event) => event.key === "Escape" && !saving && onClose();
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose, saving]);

  const setField = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const setSocial = (key, value) =>
    setForm((current) => ({ ...current, socials: { ...current.socials, [key]: value } }));
  const setChannel = (key, value) =>
    setForm((current) => ({ ...current, channels: { ...current.channels, [key]: value } }));

  const handleLogoPick = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      const dataUrl = await readImageAsDataUrl(file);
      setField("logo", dataUrl);
    } catch (err) {
      showError(err.message || "Could not read logo.");
    }
  };

  const businessTypeSelectValue = TYPES.includes(form.business_type)
    ? form.business_type
    : TYPE_OTHER;

  const displayLogo = resolveMediaUrl(form.logo || institution.logo);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-primary/40 p-4 backdrop-blur-sm sm:items-center">
      <div role="dialog" aria-modal="true" className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-primary/[0.07] bg-white px-5 py-4">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-primary/[0.07] text-primary ring-1 ring-primary/10">
              {displayLogo ? (
                <img
                  src={displayLogo}
                  alt=""
                  className="h-full w-full object-contain"
                />
              ) : (
                <Pencil className="h-4 w-4" />
              )}
            </span>
            <div>
              <h2 className="font-bold text-primary">Edit institution</h2>
              <p className="text-xs text-primary/45">
                {institution.legal_name || institution.name}
                {institution.code ? ` · ${institution.code}` : ""}
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} disabled={saving} aria-label="Close" className="rounded-lg p-2 text-primary/45 hover:bg-primary/[0.05]">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid gap-4 p-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <p className="mb-1.5 text-xs font-semibold text-primary/50">Institution logo</p>
            <div className="flex flex-wrap items-center gap-4 rounded-xl border border-primary/10 bg-primary/[0.02] p-4">
              <span className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white ring-1 ring-primary/10">
                {displayLogo ? (
                  <img src={displayLogo} alt="" className="h-full w-full object-contain" />
                ) : (
                  <Camera className="h-6 w-6 text-primary/30" />
                )}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-xs text-primary/50">PNG, JPG, or WebP up to 2 MB.</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-primary/15 bg-white px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/[0.03]">
                    <Camera className="h-3.5 w-3.5" />
                    {form.logo ? "Change logo" : "Upload logo"}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="sr-only"
                      onChange={handleLogoPick}
                    />
                  </label>
                  {form.logo ? (
                    <button
                      type="button"
                      onClick={() => setField("logo", null)}
                      className="rounded-lg px-3 py-1.5 text-xs font-semibold text-primary/50 hover:bg-primary/[0.04] hover:text-primary"
                    >
                      Remove
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
          <Input label="Legal name" value={form.legal_name} onChange={(e) => setField("legal_name", e.target.value)} />
          <div>
            <Select
              label="Business type"
              value={businessTypeSelectValue}
              onChange={(value) => {
                if (value === TYPE_OTHER) {
                  setField(
                    "business_type",
                    TYPES.includes(form.business_type) ? "" : form.business_type
                  );
                  return;
                }
                setField("business_type", value);
              }}
              options={TYPE_OPTIONS}
            />
            {businessTypeSelectValue === TYPE_OTHER ? (
              <Input
                containerClassName="mt-3"
                label="Specify business type"
                value={form.business_type}
                onChange={(e) => setField("business_type", e.target.value)}
                placeholder="e.g. Insurance, Fintech, Telecom"
              />
            ) : null}
          </div>
          <Select
            label="Region"
            value={form.region}
            onChange={(value) => setField("region", value)}
            options={REGIONS.map((value) => ({ value, label: value }))}
          />
          <Input label="City" value={form.city} onChange={(e) => setField("city", e.target.value)} />
          <Input
            label="GPS / physical address"
            value={form.gps_address}
            onChange={(e) => setField("gps_address", e.target.value)}
            containerClassName="sm:col-span-2"
          />

          <h3 className="border-t border-primary/[0.06] pt-4 text-xs font-bold uppercase tracking-wider text-primary/45 sm:col-span-2">
            Portal login
          </h3>
          <Input label="Primary email" type="email" value={form.primary_email} onChange={(e) => setField("primary_email", e.target.value)} />
          <Input label="Primary phone" placeholder="024XXXXXXX" value={form.primary_phone_number} onChange={(e) => setField("primary_phone_number", e.target.value)} />

          <h3 className="border-t border-primary/[0.06] pt-4 text-xs font-bold uppercase tracking-wider text-primary/45 sm:col-span-2">
            Contact details
          </h3>
          <Input label="Secondary email" type="email" value={form.secondary_email} onChange={(e) => setField("secondary_email", e.target.value)} />
          <Input label="Secondary phone" placeholder="024XXXXXXX" value={form.secondary_phone_number} onChange={(e) => setField("secondary_phone_number", e.target.value)} />
          <Input label="Website" value={form.website} onChange={(e) => setField("website", e.target.value)} containerClassName="sm:col-span-2" />

          <h3 className="border-t border-primary/[0.06] pt-4 text-xs font-bold uppercase tracking-wider text-primary/45 sm:col-span-2">
            Outreach channels
          </h3>
          <div className="grid gap-3 sm:col-span-2 sm:grid-cols-3">
            {OUTREACH_CHANNELS.map(({ key, label, icon: Icon, iconTone }) => {
              const selected = form.channels[key];
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setChannel(key, !selected)}
                  className={`flex items-center gap-3 rounded-xl border px-3 py-3 text-left transition-all ${
                    selected
                      ? "border-primary/25 bg-primary/[0.04] ring-2 ring-primary/10"
                      : "border-primary/10 bg-white hover:bg-primary/[0.02]"
                  }`}
                >
                  <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${iconTone}`}>
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="flex-1 text-sm font-semibold text-primary">{label}</span>
                  {selected ? <Check className="h-4 w-4 text-primary" strokeWidth={3} /> : null}
                </button>
              );
            })}
          </div>

          <h3 className="border-t border-primary/[0.06] pt-4 text-xs font-bold uppercase tracking-wider text-primary/45 sm:col-span-2">
            Socials
          </h3>
          {SOCIAL_FIELDS.map(({ key, label, icon: Icon, tone }) => (
            <div key={key} className="flex items-start gap-3">
              <span className={`mt-6 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${tone}`}>
                <Icon className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <Input label={label} value={form.socials[key]} onChange={(e) => setSocial(key, e.target.value)} />
              </div>
            </div>
          ))}
        </div>

        <div className="sticky bottom-0 flex justify-end gap-2 border-t border-primary/[0.06] bg-white px-5 py-4">
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" size="sm" disabled={!hasChanges || saving} onClick={() => onSave(changes)}>
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}
